// src/pages/TopDeals.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FiChevronLeft, FiChevronRight, FiPercent } from "react-icons/fi";
import { toast } from "react-toastify";
import { useLanguage } from "../i18n/LanguageContext";

import { dealAPI } from "../services/api";
import DealCard from "../components/deals/DealCard";
import Loading from "../components/common/Loading";

const readTotalFromResponse = (res) => {
  try {
    const payload = res?.data ?? {};
    if (payload.totalItems ?? payload.total) return Number(payload.totalItems ?? payload.total);
    if (payload.meta && (payload.meta.total ?? payload.meta.totalItems)) return Number(payload.meta.total ?? payload.meta.totalItems);
    if (payload.pagination && (payload.pagination.total ?? payload.pagination.totalItems)) return Number(payload.pagination.total ?? payload.pagination.totalItems);
    if (payload.data && (payload.data.total ?? payload.data.totalItems)) return Number(payload.data.total ?? payload.data.totalItems);
    const headerTotal = res?.headers?.["x-total-count"] || res?.headers?.["X-Total-Count"];
    if (headerTotal) return Number(headerTotal);
  } catch {}
  return 0;
};

const getDiscountPercent = (deal) => {
  const direct = Number(deal?.discountPercentage);
  if (Number.isFinite(direct) && direct > 0) return Math.round(direct);

  const original = Number(deal?.originalPrice ?? deal?.priceOriginal ?? 0);
  const discounted = Number(deal?.discountedPrice ?? deal?.price ?? 0);

  if (original > 0 && discounted >= 0 && discounted <= original) {
    return Math.round(((original - discounted) / original) * 100);
  }
  return 0;
};

const TopDeals = () => {
  const { t } = useLanguage();
  const mountedRef = useRef(true);

  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(12);
  const [total, setTotal] = useState(0);

  const [minDiscount, setMinDiscount] = useState(50);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const load = useCallback(async (p = page, l = limit, minD = minDiscount) => {
    setLoading(true);
    try {
      const res = await dealAPI.getAll({
        page: p,
        limit: l,
        sort: "discount",
        minDiscount: minD
      });

      const payload = res?.data ?? res;
      const list = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];
      
      // Trier côté front par pourcentage de réduction
      const sorted = list
        .map((d) => ({ ...d, _promo: getDiscountPercent(d) }))
        .sort((a, b) => (b._promo ?? 0) - (a._promo ?? 0));

      if (!mountedRef.current) return;

      setDeals(sorted);
      const t = readTotalFromResponse(res);
      setTotal(t || sorted.length || 0);

      const serverPage = payload.currentPage ?? payload.page ?? payload.data?.currentPage ?? null;
      const serverLimit = payload.itemsPerPage ?? payload.limit ?? payload.data?.itemsPerPage ?? null;
      if (serverPage) setPage(Number(serverPage));
      if (serverLimit) setLimit(Number(serverLimit));
    } catch (e) {
      console.error(e);
      toast.error("Impossible de charger les Top Deals");
      if (mountedRef.current) {
        setDeals([]);
        setTotal(0);
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [page, limit, minDiscount]);

  useEffect(() => {
    load(page, limit, minDiscount);
  }, [load, page, limit, minDiscount]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((total || deals.length) / (limit || 1))),
    [total, deals.length, limit]
  );

  const goToPage = (p) => {
    const next = Math.max(1, Math.min(totalPages, p));
    if (next === page) return;
    setPage(next);
  };

  return (
    <div className="bg-white">
      <section className="bg-[#fff3f8] py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border">
            <FiPercent className="text-[#e11d7b]" />
            <span className="font-semibold text-gray-800">Top Deals</span>
          </div>

          <h1 className="mt-4 text-2xl md:text-3xl font-extrabold text-[#e11d7b]">
            Meilleures offres par pourcentage
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Les deals les plus avantageux triés du plus grand au plus petit.
          </p>

          <div className="mt-5 flex flex-col sm:flex-row items-center justify-center gap-3">
            <div className="text-sm text-gray-700">
              Minimum remise:
            </div>
            <select
              value={minDiscount}
              onChange={(e) => { setPage(1); setMinDiscount(Number(e.target.value)); }}
              className="bg-white border rounded-lg px-3 py-2"
            >
              {[30, 40, 50, 60, 70, 80, 90].map((n) => (
                <option key={n} value={n}>{n}%+</option>
              ))}
            </select>

            <Link
              to="/deals"
              className="text-[#e11d7b] font-semibold hover:underline"
            >
              Voir tous les deals →
            </Link>
          </div>
        </div>
      </section>

      <section className="py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="mt-6"><Loading /></div>
          ) : deals.length === 0 ? (
            <div className="text-center mt-6 text-gray-500">
              Aucun deal trouvé pour {minDiscount}%+.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {deals.map((deal) => (
                  <DealCard key={deal._id ?? deal.id} deal={deal} showAction />
                ))}
              </div>

              <div className="mt-8 flex items-center justify-center gap-2">
                <button
                  type="button"
                  onClick={() => goToPage(page - 1)}
                  disabled={page <= 1 || loading}
                  className="px-3 py-2 bg-white border rounded-lg disabled:opacity-50 flex items-center gap-1"
                >
                  <FiChevronLeft /> <span>{t('common.prevShort')}</span>
                </button>

                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }).map((_, idx) => {
                    const start = Math.max(1, Math.min(totalPages - 4, page - 2));
                    const p = start + idx;
                    if (p > totalPages) return null;
                    return (
                      <button
                        key={p}
                        type="button"
                        onClick={() => goToPage(p)}
                        className={`w-9 h-9 rounded-lg border text-sm font-semibold ${
                          p === page
                            ? "bg-[#e11d7b] text-white border-[#e11d7b]"
                            : "bg-white text-gray-700"
                        }`}
                      >
                        {p}
                      </button>
                    );
                  })}
                </div>

                <button
                  type="button"
                  onClick={() => goToPage(page + 1)}
                  disabled={page >= totalPages || loading}
                  className="px-3 py-2 bg-white border rounded-lg disabled:opacity-50 flex items-center gap-1"
                >
                  <span>{t('common.nextShort')}</span> <FiChevronRight />
                </button>
              </div>

              <div className="mt-3 flex justify-center">
                <div className="text-sm text-gray-600 flex items-center gap-2">
                  <span>{t('common.show')}</span>
                  <select
                    value={limit}
                    onChange={(e) => { setPage(1); setLimit(Number(e.target.value)); }}
                    className="bg-white border rounded-lg px-2 py-1"
                  >
                    {[6, 9, 12, 18, 24].map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                  <span>{t('common.perPage')}</span>
                </div>
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default TopDeals;